import { ARENA } from '../../config/constants.js';

const CONTACT_COOLDOWN_DURATION = 0.5; // seconds between contact ticks while overlapping the player
const HIT_FLASH_DURATION = 0.08;

/**
 * A courtier summoned by The Famished Court's Feast (see patterns/TheFeast.js)
 * — the one minion in the game that does NOT chase the player: it marches
 * straight for the BOSS, and if it arrives it is devoured, healing the
 * Court. The player is a moving obstacle to it, nothing more (mild contact
 * damage if walked through). Killing it first is the entire point.
 *
 * Unlike wave minions (driven by MinionWaveSystem), courtiers exist during
 * the boss stage where nothing updates CombatSystem.minions — TheFeast
 * pattern that spawned them drives update() and removes them when they die,
 * are devoured, or the feast ends. Same takeDamage/minion:* event shape as
 * Skirmisher so the whole hit/XP/feedback pipeline works unchanged.
 */
export class Courtier {
  constructor(eventBus, { x, y, hp, moveSpeed, contactDamage }) {
    this.eventBus = eventBus;
    this.type = 'courtier';
    this.x = x;
    this.y = y;
    this.radius = 5;
    this.maxHp = hp;
    this.hp = hp;
    this.moveSpeed = moveSpeed;
    this.contactDamage = contactDamage;
    this._contactCooldown = 0;
    this.isDead = false;
    this.hitFlashTimer = 0;
  }

  /** Driven by TheFeast (not MinionWaveSystem) — marches toward the boss, ignoring the player's position entirely. */
  update(dt, player, boss) {
    this.hitFlashTimer = Math.max(0, this.hitFlashTimer - dt);

    const dx = boss.x - this.x;
    const dy = boss.y - this.y;
    const dist = Math.hypot(dx, dy) || 1;
    this.x += (dx / dist) * this.moveSpeed * dt;
    this.y += (dy / dist) * this.moveSpeed * dt;

    this.x = Math.min(ARENA.x + ARENA.width - this.radius, Math.max(ARENA.x + this.radius, this.x));
    this.y = Math.min(ARENA.y + ARENA.height - this.radius, Math.max(ARENA.y + this.radius, this.y));

    this._contactCooldown = Math.max(0, this._contactCooldown - dt);
    const playerDist = Math.hypot(player.x - this.x, player.y - this.y);
    if (this._contactCooldown <= 0 && playerDist < this.radius + player.radius) {
      player.takeDamage(this.contactDamage, this.type);
      this._contactCooldown = CONTACT_COOLDOWN_DURATION;
    }
  }

  /** Same shape as Skirmisher.takeDamage — used by CombatSystem's shared enemy collision loop. */
  takeDamage(amount) {
    if (this.isDead) return;
    const hpBefore = this.hp;
    this.hp = Math.max(0, this.hp - amount);
    const appliedDamage = hpBefore - this.hp;
    this.hitFlashTimer = HIT_FLASH_DURATION;
    this.eventBus.emit('minion:damaged', { amount: appliedDamage, x: this.x, y: this.y, minion: this });
    if (this.hp <= 0) {
      this.isDead = true;
      this.eventBus.emit('minion:died', { x: this.x, y: this.y, minion: this });
    }
  }
}
